import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import type { AdminExpertUser } from '@services/admin-users.service.ts';
import { pmUsersService } from '@services/pm-users.service.ts';
import { PlanChip } from '@components/admin/PlanChip.tsx';
import { ConnectionsByStatusChips } from '@components/admin/ConnectionsByStatusChips.tsx';
import { UserStatusChip } from '@components/admin/UserStatusChip.tsx';
import { toastService } from '@services/toast.service.ts';

// Експерти для продакт-менеджера (2026-10-10). Окремий клон адмінської сторінки /admin/users/experts лише з основою:
// список, пошук, статус, перехід у картку експерта (/pm/expert/:id). Дії адміна (деталі, блокування, тариф) і статус Telegram свідомо не перенесені - сторінку
// доробляємо під продакта окремо.
const PmExpertsPageContent: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [experts, setExperts] = useState<AdminExpertUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchExperts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await pmUsersService.getExperts({
        page,
        limit,
        search: searchQuery || undefined,
        status: statusFilter ? Number(statusFilter) : undefined,
      });
      setExperts(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список експертів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, statusFilter]);

  useEffect(() => {
    fetchExperts();
  }, [fetchExperts]);

  return (
    <Box data-i-doc={PageIndices.PM_EXPERTS}>
      <PageHint iDoc={PageIndices.PM_EXPERTS} />

      <PageCard>
        <PageToolbar
          storageKey="filters:pm-experts"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем або email',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'status',
              type: 'select',
              label: 'Статус',
              value: statusFilter,
              onChange: (v) => { setStatusFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: '1', label: 'Активний' },
                { value: '2', label: 'Заблокований' },
                { value: '3', label: 'Видалений' },
              ],
            },
          ]}
          onReset={() => { setSearchQuery(''); setStatusFilter(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : experts.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Експертів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Аватар</TableCell>
                  <TableCell>Ім'я / Email / Телефон</TableCell>
                  <TableCell>Часовий пояс</TableCell>
                  <TableCell>Рейтинг</TableCell>
                  <TableCell>Спеціалізації</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Клієнтів</TableCell>
                  <TableCell>Реєстрація</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experts.map((expert) => (
                  <TableRow
                    hover
                    key={expert.id}
                    onClick={() => navigate(`/pm/expert/${expert.id}/dashboard`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <UserAvatar path={expert.avatar} name={expert.name} sx={{ width: 32, height: 32 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{expert.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">{expert.email}</Typography>
                        {expert.isEmailVerified ? (
                          <Tooltip title="Email верифіковано">
                            <CheckCircleOutline color="success" sx={{ fontSize: 13 }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Email не верифіковано">
                            <CancelOutlined color="disabled" sx={{ fontSize: 13 }} />
                          </Tooltip>
                        )}
                      </Box>
                      {expert.phone && (
                        <Typography variant="caption" color="text.secondary">{expert.phone}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>{expert.timezone ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating value={expert.rating} precision={0.1} readOnly size="small" />
                        <Typography variant="body2" color="text.secondary">
                          {expert.rating != null ? expert.rating.toFixed(1) : '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {expert.specializations.length > 0
                          ? expert.specializations.map((s) => (
                              <Chip key={s} label={s} size="small" variant="outlined" />
                            ))
                          : '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <UserStatusChip status={expert.status} isProtectedDemoAccount={expert.isProtectedDemoAccount} />
                    </TableCell>
                    <TableCell>
                      <PlanChip subscription={expert.subscription} />
                    </TableCell>
                    <TableCell><ConnectionsByStatusChips connectionsByStatus={expert.connectionsByStatus} /></TableCell>
                    <TableCell>{new Date(expert.createdAt).toLocaleDateString('uk-UA')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:pm-experts"
        />
      </PageCard>
    </Box>
  );
};

export const PmExpertsPage: React.FC = () => (
  <DashboardLayout>
    <PmExpertsPageContent />
  </DashboardLayout>
);
